/**
 * Redaction + fingerprinting for frontend error reports.
 *
 * Goal: never persist user PII or credentials in `public.frontend_error_events`,
 * while keeping enough structure (shape of the value, field name, stack frames)
 * that the error is still debuggable.
 */

type Rule = { re: RegExp; replace: string | ((...m: string[]) => string) };

const RULES: Rule[] = [
  // Emails -> drop entirely
  { re: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, replace: "[email]" },
  // JWTs (three base64url segments)
  { re: /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g, replace: "[jwt]" },
  // Known provider key prefixes
  { re: /\b(sk|pk|rk|whsec|lovack|sbp|ghp|gho|xoxb|xoxp)[-_][A-Za-z0-9_-]{8,}/gi, replace: "[key]" },
  // Sensitive key/value pairs in JSON, query strings, or logs (keep the field name)
  {
    re: /("|\b)(password|passwd|secret|token|access_token|refresh_token|api_?key|apikey|authorization|auth|session|cookie|otp|code|credit_card|card_number|cvv|ssn)("?\s*[:=]\s*)("?)(?:Bearer\s+)?(?!\[)[^",&\s}]{1,512}/gi,
    replace: (_m: string, q1: string, field: string, sep: string, q2: string) =>
      `${q1}${field}${q1 === '"' ? '"' : ""}${sep}${q2}[redacted]`,
  },
  // Bare bearer tokens
  { re: /\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi, replace: "Bearer [token]" },
  // UUIDs -> keep the shape, drop the identity (before digit runs)
  {
    re: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    replace: "[uuid]",
  },
  // Phone numbers (loose international)
  { re: /\+\d[\d\s().-]{7,}\d/g, replace: "[phone]" },
  // Long digit runs (card / account numbers)
  { re: /\b\d{12,19}\b/g, replace: "[number]" },
];

/** Redact secrets/PII from a free-text field (message, stack, source, UA). */
export function redactText(input: string | null | undefined): string | null {
  if (input == null) return null;
  let out = String(input);
  for (const rule of RULES) {
    out = out.replace(rule.re, rule.replace as never);
  }
  return out;
}

/**
 * Redact a URL while keeping route + param names: query values, hash values and
 * path segments that look like identifiers are replaced with placeholders.
 */
export function redactUrlLike(input: string | null | undefined): string | null {
  if (input == null) return null;
  const raw = String(input);
  const redactQuery = (qs: string) =>
    qs
      .split("&")
      .map((pair) => {
        if (!pair) return pair;
        const [k, ...rest] = pair.split("=");
        return rest.length ? `${k}=[redacted]` : k;
      })
      .join("&");

  let out = raw
    .replace(/\?([^#]*)/, (_m, qs) => `?${redactQuery(qs)}`)
    .replace(/#([^?]*=[^?]*)$/, (_m, hs) => `#${redactQuery(hs)}`);

  // Opaque-looking path segments (uuid, long hex/base64, digits) become :id
  out = out.replace(
    /\/(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-zA-Z_-]{20,}|\d{4,})(?=$|[/?#])/gi,
    "/:id",
  );
  return redactText(out);
}

/** Stable non-crypto 32-bit hash rendered as hex (FNV-1a). */
function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** Strip variable parts of a message so the same crash groups together. */
export function normalizeMessage(message: string): string {
  return (message || "")
    .replace(/\d+/g, "N")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 300)
    .toLowerCase();
}

/** First N meaningful stack frames, with paths/queries normalized. */
export function normalizeStack(stack: string | null | undefined, frames = 3): string {
  if (!stack) return "";
  return stack
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("at ") || /\.[jt]sx?:/.test(l))
    .slice(0, frames)
    .map((l) =>
      l
        .replace(/https?:\/\/[^\s)]+?(?=\/)/g, "")
        .replace(/\?[^\s):]*/g, "")
        .replace(/:\d+:\d+/g, ":L:C"),
    )
    .join("|");
}

/**
 * Deduped stack fingerprint: same crash across sessions/users/releases yields
 * the same 16-char id so the admin UI can group and count it.
 */
export function fingerprintError(input: {
  errorType: string;
  message: string;
  source?: string | null;
  stack?: string | null;
}): string {
  const source = (input.source ?? "").replace(/^https?:\/\/[^/]+/, "").replace(/\?.*$/, "");
  const basis = [
    input.errorType,
    normalizeMessage(input.message),
    source,
    normalizeStack(input.stack),
  ].join("§");
  return `${fnv1a(basis)}${fnv1a(basis.split("").reverse().join(""))}`;
}
